import React, { useState, useEffect } from 'react';
import {
Container,
Typography,
Button,
Paper,
TextField,
Dialog,
DialogTitle,
DialogContent,
DialogActions,
IconButton,
Box,
Chip,
Divider,
useTheme,
Grid,
Slide,
MenuItem,
InputLabel,
FormControl,
Select,
TableContainer,
Table,
TableHead,
TableRow,
TableCell,
TableBody,
Card,
CardContent,
Tabs,
Tab,
Alert,
LinearProgress,
CircularProgress,
Tooltip,
Menu
} from '@mui/material';
import {
PictureAsPdf,
TableChart,
GetApp,
FilterList,
Refresh,
Today,
Close,
Search,
Download,
TrendingUp,
TrendingDown,
Inventory,
AttachMoney,
Storage,
Warning,
CheckCircle,
Error,
CalendarMonth,
BarChart,
PieChart,
Summarize
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import axios from 'axios';
import useCustomToast from '../../../hooks/useToast';
import {APIBarStockReport,} from '../../../services/APIs';
import { TEXT_CONNECTION_ERROR, TEXT_DATA_LOADED } from '../../../services/Text';
import {
formatNumberWithComma,
getCategoriesNameById,
getUserDetails,
formatCreatedAtDate,
formatCurrency
} from '../../../utils/helpers';
import { exportStockReportToExcel, exportStockReportToPDF, exportStockReportToCSV } from '../../../services/BarStockReportService';
import { fetchBranchs, fetchCounters } from '../../../utils/namesListUtils';
import { getUserRole, PERMISSIONS, ROLE_PERMISSIONS } from '../../../utils/Permission';

const StockReport = () => {
const theme = useTheme();
const [loading, setLoading] = useState(false);
const [error, setError] = useState(null);
const showToast = useCustomToast();
const [exportAnchorEl, setExportAnchorEl] = useState(null);

const [reportData, setReportData] = useState({
    summary: {},
    details: [],
    trends: [],
    topItems: [],
    lowStockItems: [],
    categories: [],
    dateRange: {}
});

const [filters, setFilters] = useState({
    branch_id: '',
    stock_level: '',
    status: '',
    dateFrom: '',
    dateTo: '',
    report_type: 'detailed', // detailed, summary, trends
    sort_by: 'balance', // balance, value, profit
    sort_order: 'desc' // asc, desc
});

const [branchNames, setBranchNames] = useState([]);
const [selectedBranch, setSelectedBranch] = useState('');
const [activeTab, setActiveTab] = useState(0);
const [exportDialogOpen, setExportDialogOpen] = useState(false);
const [chartType, setChartType] = useState('bar'); // bar, pie, line
const [userRole, setUserRole] = useState('');

useEffect(() => {
    const role = getUserRole();
    setUserRole(role);
    fetchBranchNames();
    generateReport();
}, []);

const hasPermission = (permission) => {
    return ROLE_PERMISSIONS[userRole]?.includes(permission) || false;
};

const fetchBranchNames = async () => {
    try {
    const data = await fetchBranchs();
    setBranchNames(data || []);
    } catch (error) {
    showToast("Error", error.message, "error");
    }
};


const generateReport = async () => {
    setLoading(true);
    setError(null);

    try {
    let branchIdToUse = filters.branch_id;
    if (!hasPermission(PERMISSIONS.BRANCHES) && !branchIdToUse) {
        branchIdToUse = getUserDetails("branchId");
    }

    const params = {
        ...filters,
        branch_id: branchIdToUse || '',
        report_type: filters.report_type,
        sort_by: filters.sort_by,
        sort_order: filters.sort_order
    };

    // Clean up empty params
    Object.keys(params).forEach(key => {
        if (!params[key]) delete params[key];
    });
    const response = await axios.get(APIBarStockReport, { params });

    if (response.data.status === 'success') {
        setReportData(response.data.data);
        showToast("Success", "Report generated successfully", "success");
    } else {
        throw new Error(response.data.message || "Failed to generate report");
    }
    } catch (error) {
    let errorMessage = "Failed to generate report";
    if (error.response) {
        errorMessage = error.response.data.message || error.message;
    } else if (error.code === "ERR_NETWORK") {
        errorMessage = TEXT_CONNECTION_ERROR;
    }
    setError(errorMessage);
    showToast("Error", errorMessage, "error");
    } finally {
    setLoading(false);
    }
};

const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({
    ...prev,
    [name]: value
    }));
};

const handleDateRangeChange = (type, value) => {
    setFilters(prev => ({
    ...prev,
    [type]: value
    }));
};

const applyToday = () => {
    const today = new Date().toISOString().split('T')[0];
    setFilters({
    ...filters,
    dateFrom: today,
    dateTo: today
    });
};

const applyThisWeek = () => {
    const today = new Date();
    const firstDay = new Date(today.setDate(today.getDate() - today.getDay()));
    const lastDay = new Date(today.setDate(today.getDate() - today.getDay() + 6));
    
    setFilters({
    ...filters,
    dateFrom: firstDay.toISOString().split('T')[0],
    dateTo: lastDay.toISOString().split('T')[0]
    });
};

const applyThisMonth = () => {
    const today = new Date();
    const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);
    const lastDay = new Date(today.getFullYear(), today.getMonth() + 1, 0);
    
    setFilters({
    ...filters,
    dateFrom: firstDay.toISOString().split('T')[0],
    dateTo: lastDay.toISOString().split('T')[0]
    });
};

const clearFilters = () => {
    setFilters({
    branch_id: '',
    stock_level: '',
    status: '',
    dateFrom: '',
    dateTo: '',
    report_type: 'detailed',
    sort_by: 'balance',
    sort_order: 'desc'
    });
    setSelectedBranch('');
};

const handleExportClick = (event) => {
    setExportAnchorEl(event.currentTarget);
};

const handleExportClose = () => {
    setExportAnchorEl(null);
};

const handleExportExcel = async () => {
    try {
    handleExportClose();
    await exportStockReportToExcel(reportData, filters);
    showToast("Success", "Excel report downloaded successfully", "success");
    } catch (error) {
    console.error('Excel export error:', error);
    showToast("Error", error.message, "error");
    }
};

const handleExportPDF = async () => {
    try {
    handleExportClose();
    await exportStockReportToPDF(reportData, filters);
    showToast("Success", "PDF report downloaded successfully", "success");
    } catch (error) {
    console.error('PDF export error:', error);
    showToast("Error", error.message, "error");
    }
};

const handleExportCSV = async () => {
    try {
    handleExportClose();
    await exportStockReportToCSV(reportData, filters);
    showToast("Success", "CSV report downloaded successfully", "success");
    } catch (error) {
    console.error('CSV export error:', error);
    showToast("Error", error.message, "error");
    }
};

const getStockLevelColor = (level) => {
    switch (level?.toLowerCase()) {
    case 'out of stock': return theme.palette.error.main;
    case 'low': return theme.palette.warning.main;
    case 'normal': return theme.palette.success.main;
    case 'high': return theme.palette.info.main;
    default: return theme.palette.grey[500];
    }
};


const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
};

if (loading && !reportData.details.length) {
    return (
    <Container maxWidth="xl" sx={{ py: 3, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <Box textAlign="center">
        <CircularProgress size={60} />
        <Typography variant="h6" sx={{ mt: 2 }}>Generating Report...</Typography>
        </Box>
    </Container>
    );
}

return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
    {/* Header */}
    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Box>
        <Typography variant="h4" sx={{ fontWeight: 'bold', color: theme.palette.primary.main }}>
            Bar Stock Analysis Report
        </Typography>
        <Typography variant="body2" color="text.secondary">
            Comprehensive stock analysis and insights
        </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 2 }}>
        <Button
            variant="contained"
            color="primary"
            startIcon={<Refresh />}
            onClick={generateReport}
        >
            Refresh Report
        </Button>
        <Button
            variant="contained"
            color="success"
            startIcon={<GetApp />}
            onClick={handleExportClick}
        >
            Export Report
        </Button>
        </Box>
    </Box>

    {/* Filters Section */}
    <Paper elevation={2} sx={{ p: 3, mb: 4, borderRadius: 3 }}>
        <Typography variant="h6" sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
        <FilterList /> Report Filters
        </Typography>
        
        <Grid container spacing={3}>
        {/* Quick Date Filters */}
        <Grid item xs={12}>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <Button
                variant="outlined"
                startIcon={<Today />}
                onClick={applyToday}
            >
                Today
            </Button>
            <Button
                variant="outlined"
                startIcon={<CalendarMonth />}
                onClick={applyThisWeek}
            >
                This Week
            </Button>
            <Button
                variant="outlined"
                startIcon={<CalendarMonth />}
                onClick={applyThisMonth}
            >
                This Month
            </Button>
            <Button
                variant="outlined"
                color="error"
                startIcon={<Close />}
                onClick={clearFilters}
            >
                Clear All
            </Button>
            </Box>
        </Grid>

        {/* Main Filters */}
        <Grid item xs={12} md={6} lg={2}>
            <FormControl fullWidth size="small">
            <InputLabel>Stock Level</InputLabel>
            <Select
                name="stock_level"
                value={filters.stock_level}
                onChange={handleFilterChange}
                label="Stock Level"
            >
                <MenuItem value="">All Levels</MenuItem>
                <MenuItem value="Out of Stock">Out of Stock</MenuItem>
                <MenuItem value="Low">Low Stock</MenuItem>
                <MenuItem value="Normal">Normal Stock</MenuItem>
                <MenuItem value="High">High Stock</MenuItem>
            </Select>
            </FormControl>
        </Grid>

        <Grid item xs={12} md={6} lg={2}>
            <FormControl fullWidth size="small">
            <InputLabel>Status</InputLabel>
            <Select
                name="status"
                value={filters.status}
                onChange={handleFilterChange}
                label="Status"
            >
                <MenuItem value="">All Status</MenuItem>
                <MenuItem value="1">Active</MenuItem>
                <MenuItem value="2">Inactive</MenuItem>
            </Select>
            </FormControl>
        </Grid>

        {/* Date Range */}
        <Grid item xs={12} md={6} lg={4}>
            <TextField
            fullWidth
            label="From Date"
            type="date"
            name="dateFrom"
            value={filters.dateFrom}
            onChange={(e) => handleDateRangeChange('dateFrom', e.target.value)}
            InputLabelProps={{ shrink: true }}
            size="small"
            />
        </Grid>
        <Grid item xs={12} md={6} lg={4}>
            <TextField
            fullWidth
            label="To Date"
            type="date"
            name="dateTo"
            value={filters.dateTo}
            onChange={(e) => handleDateRangeChange('dateTo', e.target.value)}
            InputLabelProps={{ shrink: true }}
            size="small"
            />
        </Grid>

        {/* Report Type */}
        <Grid item xs={12} md={6} lg={2}>
            <FormControl fullWidth size="small">
            <InputLabel>Report Type</InputLabel>
            <Select
                name="report_type"
                value={filters.report_type}
                onChange={handleFilterChange}
                label="Report Type"
            >
                <MenuItem value="detailed">Detailed</MenuItem>
                <MenuItem value="summary">Summary</MenuItem>
                <MenuItem value="trends">Trend Analysis</MenuItem>
            </Select>
            </FormControl>
        </Grid>

        {/* Sort Options */}
        <Grid item xs={12} md={6} lg={2}>
            <FormControl fullWidth size="small">
            <InputLabel>Sort By</InputLabel>
            <Select
                name="sort_by"
                value={filters.sort_by}
                onChange={handleFilterChange}
                label="Sort By"
            >
                <MenuItem value="balance">Stock Balance</MenuItem>
                <MenuItem value="value">Total Value</MenuItem>
                <MenuItem value="profit">Profit Margin</MenuItem>
                <MenuItem value="name">Item Name</MenuItem>
                <MenuItem value="date">Stock Date</MenuItem>
            </Select>
            </FormControl>
        </Grid>

        <Grid item xs={12} md={6} lg={2}>
            <FormControl fullWidth size="small">
            <InputLabel>Sort Order</InputLabel>
            <Select
                name="sort_order"
                value={filters.sort_order}
                onChange={handleFilterChange}
                label="Sort Order"
            >
                <MenuItem value="desc">Descending</MenuItem>
                <MenuItem value="asc">Ascending</MenuItem>
            </Select>
            </FormControl>
        </Grid>

        {/* Generate Button */}
        <Grid item xs={12}>
            <Button
            fullWidth
            variant="contained"
            color="primary"
            size="large"
            onClick={generateReport}
            startIcon={<Search />}
            sx={{ mt: 2 }}
            >
            Generate Report
            </Button>
        </Grid>
        </Grid>

        {/* Active Filters */}
        {Object.keys(filters).some(key => filters[key] && !['report_type', 'sort_by', 'sort_order'].includes(key)) && (
        <Box sx={{ mt: 3, pt: 2, borderTop: 1, borderColor: 'divider' }}>
            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
            Active Filters:
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>

            {filters.stock_level && (
                <Chip
                label={`Stock Level: ${filters.stock_level}`}
                onDelete={() => handleFilterChange({ target: { name: 'stock_level', value: '' } })}
                color="primary"
                size="small"
                />
            )}
            {filters.status && (
                <Chip
                label={`Status: ${filters.status === '1' ? 'Active' : 'Inactive'}`}
                onDelete={() => handleFilterChange({ target: { name: 'status', value: '' } })}
                color="primary"
                size="small"
                />
            )}
            {filters.dateFrom && filters.dateTo && (
                <Chip
                label={`Date: ${filters.dateFrom} to ${filters.dateTo}`}
                onDelete={() => {
                    handleFilterChange({ target: { name: 'dateFrom', value: '' } });
                    handleFilterChange({ target: { name: 'dateTo', value: '' } });
                }}
                color="primary"
                size="small"
                />
            )}
            </Box>
        </Box>
        )}
    </Paper>

    {/* Report Summary Cards */}
    {reportData.summary && (
        <Grid container spacing={3} sx={{ mb: 4 }}>
        {/* Total Items */}
        <Grid item xs={6} sm={4} md={3} lg={2}>
            <Card elevation={3}>
            <CardContent sx={{ textAlign: 'center' }}>
                <Inventory sx={{ fontSize: 40, color: theme.palette.primary.main, mb: 1 }} />
                <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                {formatNumberWithComma(reportData.summary.total_items || 0)}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                Total Items
                </Typography>
            </CardContent>
            </Card>
        </Grid>

        {/* Total Value */}
        <Grid item xs={6} sm={4} md={3} lg={2}>
            <Card elevation={3}>
            <CardContent sx={{ textAlign: 'center' }}>
                <AttachMoney sx={{ fontSize: 40, color: theme.palette.success.main, mb: 1 }} />
                <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                {formatCurrency(reportData.summary.total_value || 0)}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                Total Value
                </Typography>
            </CardContent>
            </Card>
        </Grid>


        {/* Low Stock Alert */}
        <Grid item xs={6} sm={4} md={3} lg={2}>
            <Card elevation={3} sx={{ borderColor: reportData.summary.low_stock_items > 0 ? 'warning.main' : 'success.main' }}>
            <CardContent sx={{ textAlign: 'center' }}>
                <Warning sx={{ fontSize: 40, color: theme.palette.warning.main, mb: 1 }} />
                <Typography variant="h6" sx={{ fontWeight: 'bold', color: reportData.summary.low_stock_items > 0 ? 'warning.main' : 'inherit' }}>
                {formatNumberWithComma(reportData.summary.low_stock_items || 0)}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                Low Stock Items
                </Typography>
            </CardContent>
            </Card>
        </Grid>

        {/* Out of Stock */}
        <Grid item xs={6} sm={4} md={3} lg={2}>
            <Card elevation={3} sx={{ borderColor: reportData.summary.out_of_stock_items > 0 ? 'error.main' : 'success.main' }}>
            <CardContent sx={{ textAlign: 'center' }}>
                <Error sx={{ fontSize: 40, color: theme.palette.error.main, mb: 1 }} />
                <Typography variant="h6" sx={{ fontWeight: 'bold', color: reportData.summary.out_of_stock_items > 0 ? 'error.main' : 'inherit' }}>
                {formatNumberWithComma(reportData.summary.out_of_stock_items || 0)}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                Out of Stock
                </Typography>
            </CardContent>
            </Card>
        </Grid>

        {/* Active Items */}
        <Grid item xs={6} sm={4} md={3} lg={2}>
            <Card elevation={3}>
            <CardContent sx={{ textAlign: 'center' }}>
                <CheckCircle sx={{ fontSize: 40, color: theme.palette.success.main, mb: 1 }} />
                <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                {formatNumberWithComma(reportData.summary.active_items || 0)}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                Active Items
                </Typography>
            </CardContent>
            </Card>
        </Grid>
        </Grid>
    )}

    {/* Report Tabs */}
    <Paper elevation={2} sx={{ borderRadius: 3, overflow: 'hidden' }}>
        <Tabs
        value={activeTab}
        onChange={handleTabChange}
        variant="fullWidth"
        sx={{ bgcolor: theme.palette.primary.main }}
        >
        <Tab 
            label={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'white' }}>
                <Summarize /> Detailed Report
            </Box>
            }
        />
        <Tab 
            label={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'white' }}>
                <BarChart /> Stock Analysis
            </Box>
            }
        />
        <Tab 
            label={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'white' }}>
                <PieChart /> Category Breakdown
            </Box>
            }
        />
        </Tabs>

        {/* Tab 1: Detailed Report */}
        {activeTab === 0 && (
        <Box sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ mb: 3 }}>
            Detailed Stock Report
            </Typography>
                        
            <TableContainer>
            <Table>
                <TableHead>
                <TableRow sx={{ bgcolor: theme.palette.action.hover }}>
                    <TableCell><strong>#</strong></TableCell>
                    <TableCell><strong>Item Name</strong></TableCell>
                    <TableCell><strong>Stock Level</strong></TableCell>
                    <TableCell><strong>Balance</strong></TableCell>
                    <TableCell><strong>Cost Price</strong></TableCell>
                    <TableCell><strong>Selling Price</strong></TableCell>
                    <TableCell><strong>Total Cost</strong></TableCell>
                    <TableCell><strong>Status</strong></TableCell>
                </TableRow>
                </TableHead>
                <TableBody>
                {reportData.details?.map((item, index) => (
                    <TableRow key={index} hover>
                    <TableCell>{index + 1}</TableCell>
                    <TableCell sx={{ fontWeight: 'medium' }}>{item.name}</TableCell>
                    <TableCell>
                        <Chip
                        label={item.stock_level}
                        size="small"
                        sx={{
                            bgcolor: getStockLevelColor(item.stock_level),
                            color: 'white',
                            fontWeight: 'bold'
                        }}
                        />
                    </TableCell>
                    <TableCell>
                        <Chip
                        label={formatNumberWithComma(item.balance)}
                        color={item.balance > 0 ? "success" : "error"}
                        variant="outlined"
                        size="small"
                        />
                    </TableCell>
                    <TableCell>{formatCurrency(item.cost)}</TableCell>
                    <TableCell>{formatCurrency(item.selling)}</TableCell>
                    <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                        {formatNumberWithComma(item.balance * item.cost || 0)}
                        </Typography>
                    </TableCell>
                    <TableCell>
                        <Chip
                        label={item.status === 1 ? 'Active' : 'Inactive'}
                        color={item.status === 1 ? 'success' : 'error'}
                        size="small"
                        />
                    </TableCell>
                    </TableRow>
                ))}
                {/* Footer Row in TableBody */}
                {reportData.details && reportData.details.length > 0 && (
                    <TableRow sx={{ 
                    backgroundColor: 'black',
                    '& td': {
                        color: 'white',
                        fontWeight: 'bold',
                        fontSize: '1rem',
                        borderTop: `3px solid ${theme.palette.primary.main}`,
                        padding: '16px 8px'
                    }
                    }}>
                    <TableCell colSpan={3} align="center">
                        <Typography variant="h6" sx={{ color: 'white' }}>
                        GRAND TOTAL
                        </Typography>
                    </TableCell>
                    <TableCell>
                        <Typography variant="h6" sx={{ color: 'white' }}>
                        {formatNumberWithComma(
                            reportData.details.reduce((sum, item) => sum + (parseInt(item.balance) || 0), 0)
                        )}
                        </Typography>
                    </TableCell>
                    <TableCell>
                        <Typography variant="body2" sx={{ color: 'white', fontStyle: 'italic' }}>
                        {formatNumberWithComma(
                            reportData.details.reduce((sum, item) => sum + (parseInt(item.cost) || 0), 0)
                        )}
                        </Typography>
                    </TableCell>
                    <TableCell>
                        <Typography variant="body2" sx={{ color: 'white', fontStyle: 'italic' }}>
                        {formatNumberWithComma(
                            reportData.details.reduce((sum, item) => sum + (parseInt(item.selling) || 0), 0)
                        )}
                        </Typography>
                    </TableCell>
                    <TableCell>
                        <Typography variant="h6" sx={{ 
                        color: 'white',
                        backgroundColor: theme.palette.primary.dark,
                        padding: '8px 12px',
                        borderRadius: '4px',
                        display: 'inline-block'
                        }}>
                        {formatCurrency(
                            reportData.details.reduce((sum, item) => 
                            sum + ((parseInt(item.balance) || 0) * (parseFloat(item.cost) || 0)), 0)
                        )}
                        </Typography>
                    </TableCell>
                    <TableCell>
                        <Chip
                        label="TOTAL"
                        sx={{
                            backgroundColor: theme.palette.success.dark,
                            color: 'white',
                            fontWeight: 'bold',
                            fontSize: '0.875rem',
                            height: '32px'
                        }}
                        />
                    </TableCell>
                    </TableRow>
                )}
                </TableBody>
            </Table>
            </TableContainer>

            {(!reportData.details || reportData.details.length === 0) && (
            <Box sx={{ textAlign: 'center', py: 6 }}>
                <Inventory sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
                <Typography variant="h6" color="text.secondary">
                No stock items found
                </Typography>
                <Typography variant="body2" color="text.secondary">
                Try adjusting your filters
                </Typography>
            </Box>
            )}
        </Box>
        )}

        {/* Tab 2: Stock Analysis */}
        {activeTab === 1 && (
        <Box sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ mb: 3 }}>
            Stock Analysis
            </Typography>
            
            <Grid container spacing={3}>
            {/* Top Items by Value */}
            <Grid item xs={12} md={6}>
                <Card elevation={2}>
                <CardContent>
                    <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <TrendingUp /> Top 5 Items by Value
                    </Typography>
                    {reportData.topItems?.slice(0, 5).map((item, index) => (
                    <Box key={index} sx={{ mb: 2, p: 2, bgcolor: 'action.hover', borderRadius: 1 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 'medium' }}>
                            {item.name}
                        </Typography>
                        <Typography variant="h6" color="primary">
                            {formatCurrency(item.total_value)}
                        </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
                        <Typography variant="body2" color="text.secondary">
                            Balance: {formatNumberWithComma(item.balance)}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            Unit Value: {formatCurrency(item.cost)}
                        </Typography>
                        </Box>
                        <LinearProgress 
                        variant="determinate" 
                        value={(item.total_value / (reportData.topItems?.[0]?.total_value || 1)) * 100}
                        sx={{ mt: 1, height: 8, borderRadius: 4 }}
                        />
                    </Box>
                    ))}
                </CardContent>
                </Card>
            </Grid>

            {/* Low Stock Alerts */}
            <Grid item xs={12} md={6}>
                <Card elevation={2}>
                <CardContent>
                    <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Warning /> Low Stock Alerts
                    </Typography>
                    {reportData.lowStockItems?.slice(0, 5).map((item, index) => (
                    <Box key={index} sx={{ mb: 2, p: 2, bgcolor: 'warning.light', borderRadius: 1 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 'medium' }}>
                            {item.name}
                        </Typography>
                        <Chip
                            label={formatNumberWithComma(item.balance)}
                            color="warning"
                            size="small"
                        />
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
                        <Typography variant="body2">
                            Level: {item.stock_level}
                        </Typography>
                        </Box>
                        <Alert severity="warning" sx={{ mt: 1 }}>
                        Reorder recommended
                        </Alert>
                    </Box>
                    ))}
                </CardContent>
                </Card>
            </Grid>
            </Grid>
        </Box>
        )}

        {/* Tab 3: Category Breakdown */}
        {activeTab === 2 && (
        <Box sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ mb: 3 }}>
            Category Breakdown
            </Typography>
            
            <Grid container spacing={3}>
            {reportData.categories?.map((category, index) => (
                <Grid item xs={12} sm={6} md={4} key={index}>
                <Card elevation={2}>
                    <CardContent>
                    <Typography variant="h6" sx={{ mb: 2 }}>
                        {category.name}
                    </Typography>
                    <Box sx={{ mt: 2 }}>
                        <Typography variant="caption" color="text.secondary">
                        Stock Distribution:
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                        {category.stock_levels?.map((level, idx) => (
                            <Tooltip key={idx} title={`${level.level}: ${level.count} items`}>
                                <Box
                                    sx={{
                                    flex: level.percentage,
                                    height: 20,
                                    color:'white',
                                    bgcolor: getStockLevelColor(level.level),
                                    borderRadius: 1
                                    }}
                                />
                                {level.count} items
                            </Tooltip>
                        ))}
                        </Box>
                    </Box>
                    </CardContent>
                </Card>
                </Grid>
            ))}
            </Grid>
        </Box>
        )}
    </Paper>

    {/* Export Menu */}
    <Menu
        anchorEl={exportAnchorEl}
        open={Boolean(exportAnchorEl)}
        onClose={handleExportClose}
    >
        <MenuItem onClick={handleExportExcel} sx={{ gap: 1 }}>
        <TableChart fontSize="small" color="success" />
        Export to Excel (.xlsx)
        </MenuItem>
        <MenuItem onClick={handleExportPDF} sx={{ gap: 1 }}>
        <PictureAsPdf fontSize="small" color="error" />
        Export to PDF (.pdf)
        </MenuItem>
        <MenuItem onClick={handleExportCSV} sx={{ gap: 1 }}>
        <TableChart fontSize="small" color="info" />
        Export to CSV (.csv)
        </MenuItem>
    </Menu>
    </Container>
);
};

export default StockReport;

const Transition = React.forwardRef(function Transition(props, ref) {
return <Slide direction="up" ref={ref} {...props} />;
});